import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import EarningsCompanyRow from "./EarningsCompanyRow";
import {
  EarningsSession,
  ROW_GAP,
  ROW_HEIGHT,
  SESSION_BODY_HEIGHT,
  SESSION_COLORS,
  SESSION_HEADER_HEIGHT,
} from "./earningsCalendarSelection";

// One session's worth of a day — "Before open" or "After close". Shared by the
// week grid and the day modal so the two never drift apart.
//
// The pair is always rendered, even when one side is empty, so the two columns
// hold the same width down the whole week and a day with only after-close
// reporters still reads as "nothing before the bell" rather than as a
// mysteriously narrow column. In the grid the body is a fixed height that
// reserves every slot whether or not it is filled; the modal shows the whole
// session and lets it run.

interface EarningsSessionColumnProps {
  session: EarningsSession;
  fixedHeight?: boolean;
}

const EarningsSessionColumn: React.FC<EarningsSessionColumnProps> = ({
  session,
  fixedHeight = false,
}) => {
  const theme = useTheme();
  const isEmpty = session.events.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${ROW_GAP}px`,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 0.25,
          height: SESSION_HEADER_HEIGHT,
        }}
      >
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: SESSION_COLORS[session.key],
            opacity: isEmpty ? 0.35 : 1,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: isEmpty
              ? theme.palette.text.disabled
              : theme.palette.text.secondary,
          }}
        >
          {session.label}
        </Typography>
        {!isEmpty && (
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              color: theme.palette.text.disabled,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {session.events.length}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: `${ROW_GAP}px`,
          minWidth: 0,
          ...(fixedHeight && { height: SESSION_BODY_HEIGHT }),
        }}
      >
        {session.events.map((event) => (
          <EarningsCompanyRow key={event.symbol} event={event} />
        ))}

        {/* An empty side still holds its place, so the pair stays aligned. The
            message sits in the first slot, level with the other side's first
            card, rather than floating in the middle of the reserved space. */}
        {isEmpty && (
          <Box
            sx={{
              height: ROW_HEIGHT,
              display: "flex",
              alignItems: "center",
              px: 0.25,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.6875rem",
                color: theme.palette.text.disabled,
              }}
            >
              None {session.label.toLowerCase()}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(EarningsSessionColumn);
